import React, { useState } from 'react';

import { Descriptions, Tag, Table, Space, Button, Modal, message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLoaderData, Link, useNavigate } from 'react-router-dom';

import ListPageHeader from '@shared/ListPageHeader';
import FulfillmentStatusLabel from '@shared/fulfillments/FulfillmentStatusLabel';
import { stockTransferOrderService } from '@services/operator/stockTransferOrderService';
import { formatDateTime } from '@utils/dateTime';

const STATUS_COLORS = {
  allocated: 'processing',
  out_of_stock: 'error',
  picking: 'orange',
  in_transit: 'blue',
  receiving: 'cyan',
  done: 'success',
  canceled: 'default'
};

const StockTransferOrderShow = () => {
  const { t } = useTranslation();
  const loaderData = useLoaderData();
  const [sto, setSto] = useState(loaderData);
  const [cancelling, setCancelling] = useState(false);
  const navigate = useNavigate();

  if (!sto) return <Spin />;

  const canEdit = sto.status === 'allocated';
  const canCancel = ['allocated', 'out_of_stock', 'picking', 'in_transit'].includes(sto.status) && sto.status !== 'canceled';
  const canReopen = sto.status === 'canceled';

  const handleCancel = () => {
    let content = t('operator.stock_transfer_orders.actions.cancel_confirm');
    if (sto.status === 'picking') {
      content = t('operator.stock_transfer_orders.actions.cancel_picking_hint');
    } else if (sto.status === 'in_transit') {
      content = t('operator.stock_transfer_orders.actions.cancel_in_transit_hint');
    }

    Modal.confirm({
      title: t('operator.stock_transfer_orders.actions.cancel'),
      content,
      okType: 'danger',
      onOk: async () => {
        setCancelling(true);
        try {
          const res = await stockTransferOrderService.cancel(sto.id);
          if (res.success) {
            message.success(t('common.update_success'));
            setSto(res.data);
          } else {
            message.error(res.message || t('common.update_failed'));
          }
        } catch (e) {
          message.error(t('common.update_failed'));
        } finally {
          setCancelling(false);
        }
      }
    });
  };

  const handleReopen = () => {
    Modal.confirm({
      title: t('operator.stock_transfer_orders.actions.reopen'),
      content: t('operator.stock_transfer_orders.actions.reopen_confirm'),
      onOk: async () => {
        try {
          const res = await stockTransferOrderService.reopen(sto.id);
          if (res.success) {
            message.success(t('common.update_success'));
            setSto(res.data);
          } else {
            message.error(res.message || t('common.update_failed'));
          }
        } catch (e) {
          message.error(t('common.update_failed'));
        }
      }
    });
  };

  const itemColumns = [
    {
      title: t('models.product.sku'),
      key: 'sku',
      render: (_, record) => (
        <Link to={`/products/${record.product?.id}`}>{record.product?.sku}</Link>
      )
    },
    {
      title: t('models.product.name'),
      key: 'name',
      render: (_, record) => (
        <Link to={`/products/${record.product?.id}`}>{record.product?.name}</Link>
      )
    },
    {
      title: t('models.stock_transfer_order_item.inventory_type'),
      dataIndex: ['inventory_type', 'name'],
      key: 'inventory_type'
    },
    {
      title: t('models.stock_transfer_order_item.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right'
    },
    {
      title: t('models.stock_transfer_order_item.batch'),
      dataIndex: 'batch',
      key: 'batch',
      render: (v) => v || '-'
    },
    {
      title: t('models.stock_transfer_order_item.expiration_date'),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      render: (v) => v || '-'
    }
  ];

  const headerActions = (
    <Space>
      {canReopen && (
        <Button type="primary" onClick={handleReopen}>
          {t('operator.stock_transfer_orders.actions.reopen')}
        </Button>
      )}
      {canEdit && (
        <Button type="primary" onClick={() => navigate(`/stock_transfer_orders/${sto.id}/edit`)}>
          {t('common.button.edit')}
        </Button>
      )}
      {canCancel && (
        <Button danger onClick={handleCancel} loading={cancelling}>
          {t('operator.stock_transfer_orders.actions.cancel')}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <ListPageHeader
        items={['stock_transfer_orders', 'stock_transfer_orders.list', { label: sto.name }]}
        title={
          <Space>
            {sto.name}
            <Tag color={STATUS_COLORS[sto.status] || 'default'}>
              {t(`enums.stock_transfer_order_status.${sto.status}`)}
            </Tag>
          </Space>
        }
        actions={headerActions}
      />

      <Descriptions bordered style={{ marginTop: 24 }} column={2}>
        <Descriptions.Item label={t('models.stock_transfer_order.barcode')}>
          {sto.barcode}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.stock_transfer_order.merchant')}>
          {sto.merchant?.name}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.stock_transfer_order.source_warehouse')}>
          {sto.source_warehouse?.name}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.stock_transfer_order.destination_warehouse')}>
          {sto.destination_warehouse?.name}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.stock_transfer_order.eta')}>
          {sto.eta || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.stock_transfer_order.shipped_at')}>
          {sto.shipped_at ? formatDateTime(sto.shipped_at) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.stock_transfer_order.completed_at')}>
          {sto.completed_at ? formatDateTime(sto.completed_at) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.stock_transfer_order.remarks')} span={2}>
          {sto.remarks || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.stock_transfer_order.creator')}>
          {sto.creator_operator?.name || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.stock_transfer_order.created_at')}>
          {formatDateTime(sto.created_at)}
        </Descriptions.Item>
      </Descriptions>

      {/* 關聯單據 */}
      {(sto.fulfillment || sto.destination_inbound) && (
        <Descriptions bordered style={{ marginTop: 24 }} column={2}
          title={t('operator.stock_transfer_orders.detail.related_documents')}
        >
          {sto.fulfillment && (
            <Descriptions.Item label={t('operator.stock_transfer_orders.detail.fulfillment')}>
              <Link to={`/fulfillments/${sto.fulfillment.id}`}>
                {sto.fulfillment.number || sto.fulfillment.id}
              </Link>
              {' '}
              <FulfillmentStatusLabel status={sto.fulfillment.status} />
            </Descriptions.Item>
          )}
          {sto.destination_inbound && (
            <Descriptions.Item label={t('operator.stock_transfer_orders.detail.destination_inbound')}>
              <Link to={`/inbounds/${sto.destination_inbound.id}`}>
                {sto.destination_inbound.barcode || sto.destination_inbound.id}
              </Link>
              {' '}
              <Tag>{t(`enums.inbound_status.${sto.destination_inbound.status}`, sto.destination_inbound.status)}</Tag>
            </Descriptions.Item>
          )}
        </Descriptions>
      )}

      {/* 項目清單 */}
      <div style={{ marginTop: 24 }}>
        <h3>{t('operator.stock_transfer_orders.detail.items')}</h3>
        <Table
          dataSource={sto.items || []}
          columns={itemColumns}
          rowKey="id"
          pagination={false}
          size="middle"
        />
      </div>
    </div>
  );
};

export default StockTransferOrderShow;
